import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Ban, CheckCircle2, Building2, Users, Eye, Hash, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { api, apiError } from "../lib/api";

const PLAN_LABEL = { free: "Gratuit", pro: "Pro", team: "Team", business: "Business", enterprise: "Entreprise" };
const SOURCE_LABEL = {
  registration: "Inscription",
  invitation: "Invitation",
  join_request: "Demande d'adhésion",
  securepass_admin: "Rattachement admin",
  legacy: "Historique",
};

export const AdminWorkspaces = () => {
  const [workspaces, setWorkspaces] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const { data } = await api.get("/admin/workspaces");
      setWorkspaces(data);
    } catch (e) {
      setLoadError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changePlan = async (workspaceId, plan) => {
    try {
      await api.post(`/admin/workspaces/${workspaceId}/plan`, { plan });
      toast.success(`Forfait mis à jour : ${PLAN_LABEL[plan]}`);
      await load();
      if (detail?.id === workspaceId) await openDetail(workspaceId);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggleSuspend = async (id) => {
    try {
      const { data } = await api.post(`/admin/workspaces/${id}/suspend`);
      toast.success(data.suspended ? "Espace suspendu" : "Espace réactivé");
      await load();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const { data } = await api.get(`/admin/workspaces/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error("Impossible de charger les détails.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const detachMember = async (userId) => {
    if (!window.confirm("Retirer ce membre de l'espace ?")) return;
    setBusy(userId);
    try {
      await api.post(`/admin/workspaces/${detail.id}/detach-user/${userId}`);
      toast.success("Membre retiré de l'espace.");
      await openDetail(detail.id);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center" data-testid="admin-workspaces-view">
        <p className="text-slate-500">Impossible de charger les espaces.</p>
        <Button onClick={load} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">Réessayer</Button>
      </div>
    );
  }
  if (!workspaces) {
    return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="space-y-6" data-testid="admin-workspaces-view"
    >
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-lg font-semibold">Espaces de travail ({workspaces.length})</h3>
        </div>

        {workspaces.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">Aucun espace pour le moment.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="workspaces-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-3 pr-4 font-semibold">Espace</th>
                  <th className="py-3 pr-4 font-semibold">Réf.</th>
                  <th className="py-3 pr-4 font-semibold">Propriétaire</th>
                  <th className="py-3 pr-4 font-semibold">Membres</th>
                  <th className="py-3 pr-4 font-semibold">Sièges</th>
                  <th className="py-3 pr-4 font-semibold">Région</th>
                  <th className="py-3 pr-4 font-semibold">Forfait</th>
                  <th className="py-3 pr-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workspaces.map((w) => (
                  <tr key={w.id} data-testid={`workspace-row-${w.id}`} className="border-b border-slate-100 last:border-0">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-900 truncate">{w.name || "—"}</div>
                      <div className="text-xs text-slate-500">{w.type === "personal" ? "Personnel" : "Organisation"}</div>
                      {w.suspended && (
                        <Badge variant="outline" className="mt-1 bg-red-500/15 text-red-600 border-red-500/30">Suspendu</Badge>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <span className="font-mono-token text-xs text-slate-600 inline-flex items-center gap-1">
                        <Hash className="w-3 h-3" />{w.public_ref || "—"}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="text-slate-700 truncate max-w-[180px]">{w.owner_name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[180px]">{w.owner_email}</div>
                    </td>
                    <td className="py-3 pr-4 text-slate-600 font-mono-token">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{w.member_count}</span>
                        {w.pending_requests > 0 && (
                          <Badge
                            data-testid={`workspace-pending-badge-${w.id}`}
                            variant="outline"
                            className="bg-amber-500/15 text-amber-600 border-amber-500/30 text-[10px]"
                            title="Demandes d'adhésion en attente"
                          >
                            {w.pending_requests} en attente
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-slate-600 font-mono-token whitespace-nowrap">{w.seats_used}/{w.seats_purchased}</td>
                    <td className="py-3 pr-4 text-slate-600 text-xs">{w.commercial_region || "—"}</td>
                    <td className="py-3 pr-4">
                      <Select value={w.plan} onValueChange={(v) => changePlan(w.id, v)}>
                        <SelectTrigger data-testid={`workspace-plan-select-${w.id}`} className="h-8 w-32 bg-slate-50 border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(PLAN_LABEL).map((p) => (
                            <SelectItem key={p} value={p}>{PLAN_LABEL[p]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {w.status_label && (
                        <div
                          data-testid={`workspace-status-${w.id}`}
                          className="mt-1 text-[10px] text-slate-500"
                        >
                          {w.status_label}
                        </div>
                      )}
                    </td>
                    <td className="py-3 pr-2 text-right whitespace-nowrap">
                      <Button
                        data-testid={`workspace-detail-btn-${w.id}`}
                        onClick={() => openDetail(w.id)}
                        size="sm" variant="outline"
                        className="border-slate-200 bg-white hover:bg-slate-100 text-slate-900 mr-2"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`workspace-suspend-btn-${w.id}`}
                        onClick={() => toggleSuspend(w.id)}
                        size="sm" variant="outline"
                        className={w.suspended
                          ? "border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600"
                          : "border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-600"}
                      >
                        {w.suspended ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl bg-white max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="workspace-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              {detail?.name || "Détails de l'espace"}
            </DialogTitle>
            <DialogDescription>Membres, sources d'adhésion et demandes en attente de cet espace.</DialogDescription>
          </DialogHeader>
          {detailLoading || !detail?.members ? (
            <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Référence publique" value={detail.public_ref} mono />
                <Info label="Forfait" value={PLAN_LABEL[detail.plan] || detail.plan} />
                <Info label="Propriétaire" value={detail.owner_email} />
                <Info label="Statut abonnement" value={detail.status_label || detail.status} />
                <Info label="Région commerciale" value={detail.commercial_region} />
                <Info label="Sièges (utilisés/achetés)" value={`${detail.seats_used}/${detail.seats_purchased}`} mono />
                <Info label="Créé le" value={detail.created_at ? new Date(detail.created_at).toLocaleDateString("fr-FR") : null} />
                <Info label="Suspendu" value={detail.suspended ? "Oui" : "Non"} />
              </div>

              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Users className="w-4 h-4" /> Membres ({detail.members.length})</h4>
                <div className="space-y-2" data-testid="workspace-detail-members">
                  {detail.members.map((m) => (
                    <div key={m.user_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm text-slate-900 truncate">{m.name || m.email}</div>
                        <div className="text-xs text-slate-500 truncate">{m.email}</div>
                      </div>
                      <div className="flex items-center flex-wrap gap-2 sm:shrink-0">
                        <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">{m.role_label}</Badge>
                        {m.source && (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/20 text-[10px]">
                            {SOURCE_LABEL[m.source] || m.source}
                          </Badge>
                        )}
                        {m.role !== "owner" && (
                          <Button
                            data-testid={`detach-member-${m.user_id}`}
                            onClick={() => detachMember(m.user_id)}
                            disabled={busy === m.user_id}
                            size="sm" variant="outline"
                            className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-600 h-7 w-7 p-0"
                            title="Retirer ce membre"
                          >
                            {busy === m.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                  <UserPlus className="w-4 h-4" /> Demandes d'adhésion en attente ({detail.pending_join_requests.length})
                </h4>
                {detail.pending_join_requests.length === 0 ? (
                  <p className="text-xs text-slate-400">Aucune demande en attente.</p>
                ) : (
                  <div className="space-y-2" data-testid="workspace-detail-join-requests">
                    {detail.pending_join_requests.map((jr) => (
                      <div key={jr.id} className="rounded-lg bg-amber-500/5 border border-amber-500/20 px-3 py-2">
                        <div className="text-sm text-slate-900">{jr.requester_name || jr.requester_email}</div>
                        <div className="text-xs text-slate-500">{jr.requester_email}</div>
                        {jr.message && <div className="text-xs text-slate-600 mt-1 italic">« {jr.message} »</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

const Info = ({ label, value, mono }) => (
  <div className="min-w-0">
    <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
    <div className={`text-slate-800 break-words ${mono ? "font-mono-token" : ""}`}>{value || "—"}</div>
  </div>
);
